import React, {MouseEvent, useEffect, useRef, useState} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";

import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {useGetReelDetailsQuery} from "../../api/get_reel_details";
import {useReelPlayer} from "../../hooks/use_reel_player";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelDetails} from "../../types/IReelDetails";
import {IReelListItem} from "../../types/IReelListItem";
import {APLA_GRADIENT_COLORS, ReelDetailsApla} from "./reel_details_parts/ReelDetailsApla";
import {ReelDetailsCenterFeedback} from "./reel_details_parts/ReelDetailsCenterFeedback";
import {ReelDetailsCloseButton} from "./reel_details_parts/ReelDetailsCloseButton";
import {ReelDetailsCtaButton} from "./reel_details_parts/ReelDetailsCtaButton";
import {ReelDetailsHeader} from "./reel_details_parts/ReelDetailsHeader";
import {ReelDetailsProgressBar} from "./reel_details_parts/ReelDetailsProgressBar";
import {ReelDetailsShareButton} from "./reel_details_parts/ReelDetailsShareButton";
import {ReelDetailsVideo} from "./reel_details_parts/ReelDetailsVideo";
import {ReelDetailsVideoControls} from "./reel_details_parts/ReelDetailsVideoControls";
import {ReelPlayButtonCover} from "./reel_details_parts/ReelPlayButtonCover";

interface IProps {
    reelId: number;
    reelDetails: IReelListItem | IReelDetails;
    shareUrl?: string;
    onVideoEnded?: (reelId: number) => void;
    showCloseBtn?: boolean;
    showHeader?: boolean;
    showCover?: boolean;
    shareDropdownWidth?: string;
    disableActionButton?: boolean;
    onClose?: () => void;
    onReady?: (reelData: IReelDetails) => void;
    handleOnClick?: () => void;
    className?: string;
}

export const ReelDetails = (props: IProps) => {
    const [showPlayCover, setShowPlayCover] = useState<boolean>(true);
    const {data: reelData} = useGetReelDetailsQuery({id: props.reelId || 0}, {skip: !props.reelId});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const player = useReelPlayer({onEnded: () => props.onVideoEnded?.(props.reelId)});
    const shareUrl = props.shareUrl;
    const creator = reelData?.creator || props.reelDetails.creator;
    const title = reelData?.title || props.reelDetails.title;
    const reelDetails = reelData || (props.reelDetails as IReelDetails);
    const gtmPayload = {reels_developer: creator.name, reels_name: title, reels_id: props.reelId};
    const cover = props.showCover ? getThumbnailLink(reelDetails?.cover_variant_url ?? reelDetails?.cover_url, 380, 680) : undefined;

    useEffect(() => {
        if (reelData) {
            props.onReady?.(reelData);
        }
    }, [reelData]);

    const onPlay = () => {
        setShowPlayCover(false);
        player.play();
    };

    const onPause = () => {
        setShowPlayCover(true);
        player.pause();
    };

    const onMute = () => {
        player.mute();
    };

    const onUnmute = () => {
        player.unmute();
    };

    const onActionButtonClick = (event?: MouseEvent) => {
        event?.stopPropagation();
        player.pause();
        reelsAnalytics.gtm.reelButtonClickEvent(gtmPayload);
    };

    const handleOnControlClick = (event: MouseEvent) => {
        event.stopPropagation();
    };

    const canShowActionButton = !props.disableActionButton && !!reelDetails?.action_button_enabled;

    return (
        <Wrapper hasOnClickAction={!!props.handleOnClick} onClick={props.handleOnClick} backgroundImgSrc={cover} className={props.className}>
            {showPlayCover && <ReelPlayButtonCover />}
            <div ref={wrapperRef} css={mediaWrapperStyle}>
                <div css={videoWrapperStyle}>
                    {player.hasBeenPlayed || player.isPlaying ? <ReelDetailsVideo ref={player.videoRef} src={reelDetails?.video} css={videoStyle} /> : null}
                </div>
                <ReelDetailsApla height="30%" zIndex={2} position="top" css={reelAplaStyle} />
                <ReelDetailsApla height="30%" zIndex={2} position="bottom" css={reelAplaStyle} />
            </div>

            <ReelDetailsCenterFeedback show={player.isLoading} delay={500}>
                <Loader size="sm" />
            </ReelDetailsCenterFeedback>

            <div css={contentStyle}>
                <div css={controlsWrapperStyle} onClick={handleOnControlClick}>
                    <ReelDetailsProgressBar videoRef={player.videoRef} />
                    <ReelDetailsVideoControls
                        isMute={player.isMuted}
                        isPlaying={player.isPlaying}
                        onMute={onMute}
                        onUnmute={onUnmute}
                        onPlay={onPlay}
                        onPause={onPause}
                    />
                    {props.showCloseBtn && <ReelDetailsCloseButton onClick={props.onClose} />}
                </div>

                {props.showHeader ? (
                    <ReelDetailsHeader creatorImageSrc={getThumbnailLink(creator.image_url, 48, 48)} creatorName={creator.name} title={title} />
                ) : null}

                <div css={[reelFooterStyle, canShowActionButton ? flex("flex-end", "space-between") : flex("flex-end", "flex-end")]}>
                    {canShowActionButton ? <ReelDetailsCtaButton reel={reelDetails} onBtnClick={onActionButtonClick} /> : null}

                    <div css={actionButtonsWrapperStyle} onClick={handleOnControlClick}>
                        {shareUrl ? (
                            <ReelDetailsShareButton
                                reelId={props.reelId}
                                shareDropdownWidth={props.shareDropdownWidth}
                                title={title}
                                creatorName={creator.name}
                                url={shareUrl}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const Wrapper = styled.div<{backgroundImgSrc?: string; hasOnClickAction?: boolean}>`
    overflow: hidden;
    position: relative;
    background: ${APLA_GRADIENT_COLORS.OUTSIDE};
    width: auto;
    height: 100%;
    transition: height 0.5s ease-in-out;
    ${({backgroundImgSrc}) => `background-image: url("${backgroundImgSrc}");`};
    background-size: cover;
    ${({hasOnClickAction}) => (hasOnClickAction ? "cursor: pointer;" : "")};
`;

const contentStyle = css`
    position: absolute;
    inset: 0;
    z-index: 1;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const mediaWrapperStyle = css`
    ${flexAbsoluteCenter};
    flex-direction: column;
    position: relative;
    z-index: 1;
    height: 100%;
`;

const videoWrapperStyle = css`
    position: absolute;
    z-index: 2;
    inset: 0;
`;

const videoStyle = css`
    display: block;
    position: relative;
    z-index: 0;
`;

const controlsWrapperStyle = css`
    ${p(2, 2, 1, 2)};
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
`;

const reelFooterStyle = css`
    flex: 1 1 auto;
    ${p(3, 2)};
    user-select: none;
    gap: ${calculateRemSize(1.5)};
    width: 100%;
`;

const actionButtonsWrapperStyle = css`
    flex: 0 0 auto;
    ${flex("center", "space-between")};
    flex-direction: row;
    gap: ${calculateRemSize(1)};
`;

const reelAplaStyle = css`
    opacity: 0.3;
`;
import React, {MouseEvent, useEffect, useRef, useState} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, flex, flexAbsoluteCenter, p} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";

import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {useGetReelDetailsQuery} from "../../api/get_reel_details";
import {useReelPlayer} from "../../hooks/use_reel_player";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelDetails} from "../../types/IReelDetails";
import {IReelListItem} from "../../types/IReelListItem";
import {APLA_GRADIENT_COLORS, ReelDetailsApla} from "./reel_details_parts/ReelDetailsApla";
import {ReelDetailsCenterFeedback} from "./reel_details_parts/ReelDetailsCenterFeedback";
import {ReelDetailsCloseButton} from "./reel_details_parts/ReelDetailsCloseButton";
import {ReelDetailsCtaButton} from "./reel_details_parts/ReelDetailsCtaButton";
import {ReelDetailsHeader} from "./reel_details_parts/ReelDetailsHeader";
import {ReelDetailsProgressBar} from "./reel_details_parts/ReelDetailsProgressBar";
import {ReelDetailsShareButton} from "./reel_details_parts/ReelDetailsShareButton";
import {ReelDetailsVideo} from "./reel_details_parts/ReelDetailsVideo";
import {ReelDetailsVideoControls} from "./reel_details_parts/ReelDetailsVideoControls";
import {ReelPlayButtonCover} from "./reel_details_parts/ReelPlayButtonCover";

interface IProps {
    reelId: number;
    reelDetails: IReelListItem | IReelDetails;
    shareUrl?: string;
    onVideoEnded?: (reelId: number) => void;
    showCloseBtn?: boolean;
    showHeader?: boolean;
    showCover?: boolean;
    shareDropdownWidth?: string;
    disableActionButton?: boolean;
    onClose?: () => void;
    onReady?: (reelData: IReelDetails) => void;
    handleOnClick?: () => void;
    className?: string;
}

export const ReelDetails = (props: IProps) => {
    const [showPlayCover, setShowPlayCover] = useState<boolean>(true);
    const {data: reelData} = useGetReelDetailsQuery({id: props.reelId || 0}, {skip: !props.reelId});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const player = useReelPlayer({onEnded: () => props.onVideoEnded?.(props.reelId)});
    const shareUrl = props.shareUrl;
    const creator = reelData?.creator || props.reelDetails.creator;
    const title = reelData?.title || props.reelDetails.title;
    const reelDetails = reelData || (props.reelDetails as IReelDetails);
    const gtmPayload = {reels_developer: creator.name, reels_name: title, reels_id: props.reelId};
    const cover = props.showCover ? getThumbnailLink(reelDetails?.cover_variant_url ?? reelDetails?.cover_url, 380, 680) : undefined;

    useEffect(() => {
        if (reelData) {
            props.onReady?.(reelData);
        }
    }, [reelData]);

    const onPlay = () => {
        setShowPlayCover(false);
        player.play();
    };

    const onPause = () => {
        setShowPlayCover(true);
        player.pause();
    };

    const onMute = () => {
        player.mute();
    };

    const onUnmute = () => {
        player.unmute();
    };

    const onActionButtonClick = (event?: MouseEvent) => {
        event?.stopPropagation();
        player.pause();
        reelsAnalytics.gtm.reelButtonClickEvent(gtmPayload);
    };

    const handleOnControlClick = (event: MouseEvent) => {
        event.stopPropagation();
    };

    const canShowActionButton = !props.disableActionButton && !!reelDetails?.action_button_enabled;

    return (
        <Wrapper hasOnClickAction={!!props.handleOnClick} onClick={props.handleOnClick} backgroundImgSrc={cover} className={props.className}>
            {showPlayCover && <ReelPlayButtonCover />}
            <div ref={wrapperRef} css={mediaWrapperStyle}>
                <div css={videoWrapperStyle}>
                    {player.hasBeenPlayed || player.isPlaying ? <ReelDetailsVideo ref={player.videoRef} src={reelDetails?.video} css={videoStyle} /> : null}
                </div>
                <ReelDetailsApla height="30%" zIndex={2} position="top" css={reelAplaStyle} />
                <ReelDetailsApla height="30%" zIndex={2} position="bottom" css={reelAplaStyle} />
            </div>

            <ReelDetailsCenterFeedback show={player.isLoading} delay={500}>
                <Loader size="sm" />
            </ReelDetailsCenterFeedback>

            <div css={contentStyle}>
                <div css={controlsWrapperStyle} onClick={handleOnControlClick}>
                    <ReelDetailsProgressBar videoRef={player.videoRef} />
                    <ReelDetailsVideoControls
                        isMute={player.isMuted}
                        isPlaying={player.isPlaying}
                        onMute={onMute}
                        onUnmute={onUnmute}
                        onPlay={onPlay}
                        onPause={onPause}
                    />
                    {props.showCloseBtn && <ReelDetailsCloseButton onClick={props.onClose} />}
                </div>

                {props.showHeader ? (
                    <ReelDetailsHeader creatorImageSrc={getThumbnailLink(creator.image_url, 48, 48)} creatorName={creator.name} title={title} />
                ) : null}

                <div css={[reelFooterStyle, canShowActionButton ? flex("flex-end", "space-between") : flex("flex-end", "flex-end")]}>
                    {canShowActionButton ? <ReelDetailsCtaButton reel={reelDetails} onBtnClick={onActionButtonClick} /> : null}

                    <div css={actionButtonsWrapperStyle} onClick={handleOnControlClick}>
                        {shareUrl ? (
                            <ReelDetailsShareButton
                                reelId={props.reelId}
                                shareDropdownWidth={props.shareDropdownWidth}
                                title={title}
                                creatorName={creator.name}
                                url={shareUrl}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const Wrapper = styled.div<{backgroundImgSrc?: string; hasOnClickAction?: boolean}>`
    overflow: hidden;
    position: relative;
    background: ${APLA_GRADIENT_COLORS.OUTSIDE};
    width: auto;
    height: 100%;
    transition: height 0.5s ease-in-out;
    ${({backgroundImgSrc}) => `background-image: url("${backgroundImgSrc}");`};
    background-size: cover;
    ${({hasOnClickAction}) => (hasOnClickAction ? "cursor: pointer;" : "")};
`;

const contentStyle = css`
    position: absolute;
    inset: 0;
    z-index: 1;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const mediaWrapperStyle = css`
    ${flexAbsoluteCenter};
    flex-direction: column;
    position: relative;
    z-index: 1;
    height: 100%;
`;

const videoWrapperStyle = css`
    position: absolute;
    z-index: 2;
    inset: 0;
`;

const videoStyle = css`
    display: block;
    position: relative;
    z-index: 0;
`;

const controlsWrapperStyle = css`
    ${p(2, 2, 1, 2)};
    ${flex("center", "space-between")};
    gap: ${calculateRemSize(3)};
`;

const reelFooterStyle = css`
    flex: 1 1 auto;
    ${p(3, 2)};
    user-select: none;
    gap: ${calculateRemSize(1.5)};
    width: 100%;
`;

const actionButtonsWrapperStyle = css`
    flex: 0 0 auto;
    ${flex("center", "space-between")};
    flex-direction: row;
    gap: ${calculateRemSize(1)};
`;

const reelAplaStyle = css`
    opacity: 0.3;
`;
